// src/pages/lkp/lookupTheme.ts
//
// Makes the lookup pages match the GenWizard design system without rewriting every
// component's CSS. All lookup components share the same CSS-variable names
// (--ink, --surface, --gov, --line, --tx, …) scoped to their root class
// (.lew .r2 .s1 .lrs .lwb .ehp*). This bridge remaps those variables to the
// theme.ts --gw-* tokens and switches the fonts to Sora / JetBrains Mono.
//
// Usage (once, at the top of the workbench):
//   import { injectLookupTheme } from "./lookupTheme";
//   injectLookupTheme();
//
// It also calls injectTheme() so the --gw-* tokens + gw-* classes exist. The
// body-prefixed selectors guarantee these win over each component's local block
// regardless of injection order.

import { T, injectTheme } from "../theme/theme";

const ROOTS = [".lew", ".r2", ".s1", ".lrs", ".lwb", '[class^="ehp-"]', '[class*=" ehp-"]'];
const scope = (decls: string) => ROOTS.map((r) => `body ${r}`).join(",") + `{${decls}}`;

const LOOKUP_BRIDGE_CSS = `
/* ── palette remap: lookup vars → GenWizard tokens ─────────────────────────── */
${scope(`
  --ink:${T.brandDark};
  --surface:${T.bgCard};
  --surface2:${T.bgSidebar};
  --line:${T.border};
  --line2:${T.border};
  --tx:${T.textPrimary};
  --tx2:${T.textMuted};
  --tx3:${T.brandMid};
  --gov:${T.green};      --gov-bg:${T.greenBg};
  --wait:${T.amber};     --wait-bg:${T.amberBg};
  --warn:${T.red};       --warn-bg:${T.redBg};
  --infer:${T.blue};     --infer-bg:${T.blueBg};
  --neutral:${T.slate};  --neutral-bg:${T.slateBg};
  font-family:'Sora',system-ui,sans-serif;
`)}

/* ── typography to match GenWizard ─────────────────────────────────────────── */
body .lew, body .r2, body .s1, body .lrs, body .lwb, body [class^="ehp-"]{
  color:${T.textPrimary};
}
/* headings that used Space Grotesk → Sora (keeps weight/size from the component) */
body .s1-title, body .r2-dh, body .ed-name, body .ehp-title, body .lrr-n{
  font-family:'Sora',system-ui,sans-serif;
}
/* mono bits → JetBrains Mono (matches gw-mono) */
body .lew .mono, body .r2 .mono, body .s1 .mono,
body [class*="-fid"], body [class*="-code"], body [class*="-key"],
body .b-fid, body .xw-code, body .xw-canon, body .s1-entin, body .r2-in, body .lrr-in{
  font-family:'JetBrains Mono','IBM Plex Mono',monospace !important;
}

/* ── surface polish so cards/inputs read like gw-card / gw-input ───────────── */
body .lew .btn.pri, body .lwb-btn.dark, body .lrs-btn.dark, body .r2-btn.dark,
body .s1-btn.ok, body .lrr-btn.pri{
  border-radius:8px;
}
`;

let done = false;
export function injectLookupTheme(): void {
  if (done || typeof document === "undefined") return;
  injectTheme();                              // ensure --gw-* tokens + gw-* classes exist
  const el = document.createElement("style");
  el.id = "lookup-theme-bridge";
  el.textContent = LOOKUP_BRIDGE_CSS;
  document.head.appendChild(el);
  done = true;
}
